import { useEffect, useState, useRef, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { Image } from "primereact/image";
import useFormState from "../../../../shared/hooks/useFormState";
import {
  getFotoFamilia,
  insertarFotoFamilia,
  editarFotoFamilia,
  eliminarFotoFamilia,
} from "../../../../services/backend/familiaService";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import AppToast from "../../../../shared/components/AppToast";
import logger from "../../../../shared/utils/logger";

export const EditarFamiliaDialog = ({
  visible,
  onCancel,
  familia,
  onConfirm,
  loading = false,
}) => {
  const { values, errors, setField, reset, validate } = useFormState(
    { nombre: "", orden: 0, ordenAuto: true },
    {
      nombre: [(v) => (!v?.trim() ? "El nombre es requerido." : null)],
    }
  );

  const toastRef = useRef(null);
  const fileUploadRef = useRef(null);
  const [fotoFamilia, setFotoFamilia] = useState(null);
  const [foto, setFoto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [fotoLoading, setFotoLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const fetchFoto = useCallback((familiaId) => {
    if (!familiaId) return;
    setFotoFamilia(null);
    setFoto(null);
    setFotoPreview(null);
    fileUploadRef.current?.clear();
    getFotoFamilia(familiaId)
      .then((res) => {
        if (!res?.error && res?.fotoFamilia) {
          setFotoFamilia(res.fotoFamilia);
          setFotoPreview(
            `data:${res.fotoFamilia.mime};base64,${res.fotoFamilia.foto}`
          );
        }
      })
      .catch((error) => {
        logger.error("No fue posible obtener la foto de la familia.", error);
      });
  }, []);

  useEffect(() => {
    if (visible && familia?.id) {
      fetchFoto(familia.id);
    }
  }, [visible, familia?.id, fetchFoto]);

  useEffect(() => {
    if (familia) {
      reset({
        nombre: familia.name || "",
        orden: familia.orden || 0,
        ordenAuto: familia.orden === 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familia?.id]);

  const handleSelectFoto = useCallback(({ files }) => {
    const file = files?.[0] || null;
    if (!file) return;
    if (file.type !== "image/jpeg" && !/\.(jpg|jpeg)$/i.test(file.name)) {
      toastRef.current?.show({
        severity: "error",
        summary: "Formato inválido",
        detail: "Solo se permiten imágenes .jpg o .jpeg.",
        life: 4000,
      });
      fileUploadRef.current?.clear();
      return;
    }
    setFoto(file);
  }, []);

  const handleEliminarFoto = () => {
    setConfirmVisible(true);
  };

  const confirmarEliminarFoto = async () => {
    try {
      await eliminarFotoFamilia(fotoFamilia.id);
      setFotoFamilia(null);
      setFotoPreview(null);
      setFoto(null);
      if (fileUploadRef.current) fileUploadRef.current.clear();
      toastRef.current?.show({
        severity: "success",
        summary: "Eliminada",
        detail: "Foto eliminada correctamente.",
        life: 3000,
      });
    } catch {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Error al eliminar la foto.",
        life: 4000,
      });
    } finally {
      setConfirmVisible(false);
    }
  };

  const handleCancel = () => {
    setFoto(null);
    setFotoPreview(null);
    setFotoFamilia(null);
    fileUploadRef.current?.clear();
    onCancel();
  };

  const handleConfirm = async () => {
    if (!validate()) return;
    setFotoLoading(true);
    try {
      if (onConfirm) {
        await onConfirm({
          ...familia,
          nombre: values.nombre.trim(),
          orden: values.ordenAuto ? 0 : values.orden,
        });
      }
      if (foto && familia?.id) {
        const formData = new FormData();
        formData.append("familia", familia.id);
        formData.append("mime", foto.type);
        formData.append("foto", foto);
        if (fotoFamilia?.id) {
          formData.append("id", fotoFamilia.id);
          formData.append("portada", "true");
          await editarFotoFamilia(formData);
        } else {
          await insertarFotoFamilia(formData);
        }
      }
      toastRef.current?.show({
        severity: "success",
        summary: "Actualizada",
        detail: "Familia actualizada correctamente.",
        life: 3000,
      });
      if (familia?.id) fetchFoto(familia.id);
    } catch (err) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: err?.body?.mensaje || err?.message || "Error al guardar.",
        life: 4000,
      });
    } finally {
      setFotoLoading(false);
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        className="p-button-text"
        label="Cancelar"
        onClick={handleCancel}
        disabled={loading}
      />
      <Button
        className="p-button"
        label="Guardar"
        disabled={loading || fotoLoading || !values.nombre.trim()}
        loading={loading || fotoLoading}
        onClick={handleConfirm}
      />
    </div>
  );

  return (
    <Dialog
      header="Editar Familia"
      visible={visible}
      onHide={handleCancel}
      footer={footer}
      style={{ width: "50vw" }}
    >
      <div className="flex flex-column gap-3">
        <div className="field">
          <label htmlFor="nombre-familia" className="block font-medium mb-2">
            Nombre de la familia
          </label>
          <InputText
            id="nombre-familia"
            value={values.nombre}
            onChange={(e) => setField("nombre", e.target.value.toUpperCase())}
            className={`w-full ${errors.nombre ? "p-invalid" : ""}`}
            placeholder="Ej: HERRAMIENTAS"
            autoFocus
          />
          {errors.nombre && <small className="p-error">{errors.nombre}</small>}
        </div>
        <div className="field">
          <label className="block font-medium mb-2">Foto de familia</label>

          {fotoFamilia && fotoPreview && (
            <div className="flex align-items-center gap-2 mb-2">
              <Image
                src={fotoPreview}
                alt="Foto actual"
                preview
                className="border-round"
                imageStyle={{ maxHeight: "120px", objectFit: "contain" }}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-danger p-button-text p-button-rounded"
                onClick={handleEliminarFoto}
                tooltip="Eliminar foto"
              />
            </div>
          )}

          <FileUpload
            ref={fileUploadRef}
            customUpload
            auto
            uploadHandler={handleSelectFoto}
            accept=".jpg,.jpeg"
            chooseLabel="Seleccionar"
            uploadOptions={{ style: { display: "none" } }}
            cancelOptions={{ style: { display: "none" } }}
            emptyTemplate={
              <p className="m-0">
                Arrastra o selecciona una foto .jpg/.jpeg (una sola).
              </p>
            }
            disabled={fotoLoading}
          />
        </div>
      </div>
      <AppToast ref={toastRef} />
      <ConfirmDialog
        visible={confirmVisible}
        header="Confirmar"
        message="¿Eliminar la foto?"
        acceptLabel="Eliminar"
        rejectLabel="Cancelar"
        acceptClassName="p-button-danger"
        onCancel={() => setConfirmVisible(false)}
        onConfirm={confirmarEliminarFoto}
      />
    </Dialog>
  );
};
